import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-ink/15 px-3 py-2.5 text-sm text-ink placeholder:text-mutedink/60 focus:outline-none focus:border-oxblood transition-colors duration-300";
const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink mb-1.5";
const pill = (on) =>
  `px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors duration-200 ${
    on
      ? "bg-ink text-parchment border-ink"
      : "border-ink/20 text-ink hover:border-oxblood hover:text-oxblood"
  }`;

const SEASONS = ["24/25", "25/26", "26/27"];

const Field = ({ id, label, required, children }) => (
  <div>
    <label htmlFor={`flag-${id}`} className={labelCls}>
      {label}
      {required && <span className="text-oxblood"> *</span>}
    </label>
    {children}
  </div>
);

const Text = ({
  id,
  value,
  onChange,
  required,
  type = "text",
  placeholder,
  testId,
}) => (
  <input
    id={`flag-${id}`}
    type={type}
    required={required}
    value={value}
    placeholder={placeholder}
    data-testid={testId || `flag-${id}-input`}
    onChange={(e) => onChange(id, e.target.value)}
    className={inputCls}
  />
);

const Select = ({ id, value, onChange, items, testId }) => (
  <select
    id={`flag-${id}`}
    value={value}
    data-testid={testId || `flag-${id}-select`}
    onChange={(e) => onChange(id, e.target.value)}
    className={inputCls}
  >
    {items.map((i) => (
      <option key={i} value={i}>
        {i}
      </option>
    ))}
  </select>
);

const EMPTY = {
  city: "",
  season: "25/26",
  division: "Open",
  gender: "Men",
  age_group: "30-34",
  ground: "White",
  quantity: 1,
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  address_line1: "",
  address_line2: "",
  city_shipping: "",
  region: "",
  postal_code: "",
  country: "",
  notes: "",
};

export const FlagOrderDialog = ({ open, onOpenChange }) => {
  const [opts, setOpts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (open && !opts)
      axios.get(`${API}/flags/options`).then(({ data }) => setOpts(data));
  }, [open, opts]);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const price = opts?.price_usd ?? 50;

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > (opts?.max_photo_mb ?? 10) * 1024 * 1024) {
      toast.error("Photo too large", {
        description: "Please choose an image under 10 MB.",
      });
      return;
    }
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setForm(EMPTY);
      setPhoto(null);
      setPreview("");
      setOrder(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) {
      toast.error("Please attach a photo of your original flag.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v !== "" && fd.append(k, v));
      fd.append("photo", photo);
      const { data } = await axios.post(`${API}/flags/orders`, fd);
      setOrder(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error("Could not submit your order", {
        description:
          typeof d === "string" ? d : "Please check the form and try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="flag-order-dialog"
        className="bg-parchment border-ink/10 p-0 sm:max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="p-6 sm:p-8">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-mutedink">
            Hyrox Flags
          </p>
          <DialogTitle className="font-serif font-light text-2xl text-ink mt-1">
            {order ? "Request received" : "Order a replica flag"}
          </DialogTitle>

          {order ? (
            <div data-testid="flag-order-success" className="mt-6 space-y-4">
              <div className="inline-flex items-center gap-2 text-oxblood">
                <Check className="w-4 h-4" />
                <span
                  data-testid="flag-order-reference"
                  className="font-mono text-sm"
                >
                  {order.reference}
                </span>
              </div>
              <p className="text-sm text-[#2A2A2A] leading-relaxed">
                {order.quantity} × Hyrox {order.city} · {order.gender}{" "}
                {order.division} · {order.age_group} · Season {order.season} ·{" "}
                {order.ground} ground — <strong>${order.total} USD</strong> plus
                shipping, quoted to your address before payment.
              </p>
              <p className="text-sm text-mutedink leading-relaxed">
                We will review the photo of your original flag first. Once
                approved you will receive an email at {order.customer_email}{" "}
                with a secure payment link. Nothing has been charged yet.
              </p>
              <button
                type="button"
                data-testid="flag-order-done-btn"
                onClick={() => close(false)}
                className="mt-2 w-full bg-ink text-parchment py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors duration-300"
              >
                Done
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="flag-order-form"
              className="mt-6 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="city" label="Race city" required>
                  <Text
                    id="city"
                    value={form.city}
                    onChange={set}
                    required
                    placeholder="Vancouver"
                  />
                </Field>
                <Field id="season" label="Season" required>
                  <Select
                    id="season"
                    value={form.season}
                    onChange={set}
                    items={SEASONS}
                  />
                </Field>
                <Field id="division" label="Division" required>
                  <Select
                    id="division"
                    value={form.division}
                    onChange={set}
                    items={opts?.divisions ?? [form.division]}
                  />
                </Field>
                <Field id="gender" label="Category" required>
                  <Select
                    id="gender"
                    value={form.gender}
                    onChange={set}
                    items={opts?.genders ?? [form.gender]}
                  />
                </Field>
                <Field id="age_group" label="Age group" required>
                  <Select
                    id="age_group"
                    value={form.age_group}
                    onChange={set}
                    items={opts?.age_groups ?? [form.age_group]}
                  />
                </Field>
                <div>
                  <p className={labelCls}>Ground colour</p>
                  <div className="flex gap-2">
                    {(opts?.grounds ?? ["White", "Black"]).map((g) => (
                      <button
                        key={g}
                        type="button"
                        data-testid={`flag-ground-${g.toLowerCase()}`}
                        onClick={() => set("ground", g)}
                        className={pill(form.ground === g)}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <p className={labelCls}>
                  Photo of your original flag{" "}
                  <span className="text-oxblood">*</span>
                </p>
                <label
                  htmlFor="flag-photo"
                  data-testid="flag-photo-dropzone"
                  className="flex items-center gap-4 border border-dashed border-ink/30 hover:border-oxblood p-4 cursor-pointer transition-colors"
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="Your original flag"
                      data-testid="flag-photo-preview"
                      className="w-16 h-20 object-cover"
                    />
                  ) : (
                    <Upload className="w-5 h-5 text-mutedink" />
                  )}
                  <span className="text-sm text-mutedink">
                    {photo
                      ? photo.name
                      : "JPG, PNG, WEBP or HEIC · under 10 MB. We check this before printing."}
                  </span>
                </label>
                <input
                  id="flag-photo"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  data-testid="flag-photo-input"
                  onChange={onPhoto}
                  className="sr-only"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="customer_name" label="Full name" required>
                  <Text
                    id="customer_name"
                    value={form.customer_name}
                    onChange={set}
                    required
                  />
                </Field>
                <Field id="customer_email" label="Email" required>
                  <Text
                    id="customer_email"
                    type="email"
                    value={form.customer_email}
                    onChange={set}
                    required
                  />
                </Field>
                <Field id="customer_phone" label="Phone">
                  <Text
                    id="customer_phone"
                    value={form.customer_phone}
                    onChange={set}
                  />
                </Field>
                <Field id="quantity" label="Quantity">
                  <Select
                    id="quantity"
                    value={form.quantity}
                    onChange={(k, v) => set(k, Number(v))}
                    items={[1, 2, 3, 4, 5]}
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field id="address_line1" label="Address" required>
                    <Text
                      id="address_line1"
                      value={form.address_line1}
                      onChange={set}
                      required
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field id="address_line2" label="Address line 2">
                    <Text
                      id="address_line2"
                      value={form.address_line2}
                      onChange={set}
                    />
                  </Field>
                </div>
                <Field id="city_shipping" label="City" required>
                  <Text
                    id="city_shipping"
                    value={form.city_shipping}
                    onChange={set}
                    required
                  />
                </Field>
                <Field id="region" label="State / Province" required>
                  <Text
                    id="region"
                    value={form.region}
                    onChange={set}
                    required
                  />
                </Field>
                <Field id="postal_code" label="Postal code" required>
                  <Text
                    id="postal_code"
                    value={form.postal_code}
                    onChange={set}
                    required
                  />
                </Field>
                <Field id="country" label="Country" required>
                  <Text
                    id="country"
                    value={form.country}
                    onChange={set}
                    required
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field id="notes" label="Notes">
                    <Text
                      id="notes"
                      value={form.notes}
                      onChange={set}
                      placeholder="Anything we should know"
                    />
                  </Field>
                </div>
              </div>

              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mutedink">
                Replica only · for athletes who own the original · not
                affiliated with Hyrox
              </p>

              <button
                type="submit"
                disabled={busy}
                data-testid="flag-order-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-ink text-parchment py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors duration-300 disabled:opacity-50"
              >
                {busy
                  ? "Sending…"
                  : `Request flag — $${price * form.quantity} USD + shipping`}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
